'use client';
import React, { FC, useEffect, useMemo, useState } from 'react';
import {
  TypeCourseChapterFields,
  TypeCoursePlayerFields,
  TypeCourseSubChapter,
} from '@/core/generated-types';
import { Entry } from 'contentful';
import { Document } from '@contentful/rich-text-types';
import {
  CourseChapter,
  CourseSupportingContent,
  CourseVideo,
  Errors,
  HeaderText,
  Loader,
  RichText,
  Tabs,
} from '@/components';
import { HeaderSize } from '@/components/ui/HeaderText';
import {
  ExpandedTabDisplayTypes,
  NavigationDisplayTypes,
} from '@/components/common/Tabs/Tabs';
import useCourseMediaAssets from '@/lib/api/playbackService/hooks/useCourseMediaAssets';
import useCoursePlaybackHistories from '@/lib/api/playbackService/hooks/useCoursePlaybackHistories';
import useTokenSetVault from '@/lib/api/playbackService/hooks/useTokenSetVault';
import { calculatePercentage } from '@/utils/calculations';
import {
  TypeSignedMuxVideoAsset,
  TypeSignedMuxVideoUpload,
} from '../CourseVideo/utils';
import useCourseStreamingQueue, {
  TypeVideoPosition,
} from './useCourseStreamingQueue';
import useCourseNavigation from './useCourseNavigation';
import { CoursePlayerPreview } from '@/components/courses/CoursePlayer/CoursePlayerPreview';
import {
  _findPlaybackHistory,
  _findVideo,
  _getInitialVideoId,
  CoursePlayerProps,
  CourseSubChapterProgressBar,
} from './utils';
import { BackgroundColorWrapper } from '@/components/common/BackgroundColorWrapper';
import { useErrorDirectives } from '@/context/ErrorDirectiveContext';
import { ERROR_KINDS } from '@/core/constants';
import { findApplicableErrorDetails } from '@/components/common/Errors/utils';
import interpolate from '@/utils/translations/interpolate';
import {
  capturePlaybackProgressEvent,
  PLAYBACK_EVENT_TYPES,
} from '@/utils/dataLayer';

// Threshold for when to start loading next video's token (50% of video duration)
const PRELOAD_THRESHOLD = 0.5;

const CoursePlayer: FC<CoursePlayerProps> = ({
  courseProduct,
  videos,
  songs,
  playbackHistoryMutations,
  playbackHistories,
  additionalSupport,
  additionalSupportSectionTitle,
  initialVideoId,
}) => {
  const courseNavigation = useCourseNavigation(courseProduct);
  const {
    activeVideoPosition,
    onDeckVideoPosition,
    activeSubChapter,
    onDeckSubChapter,
    regenerateStreamingQueueFromVideo,
    reportActiveVideoCompleted,
  } = useCourseStreamingQueue(courseProduct, initialVideoId);
  const {
    retrieveItemFromVault: retrieveVideoAssetFromVault,
    fetchTokenSetForItem: fetchTokenSetForVideoAsset,
  } = useTokenSetVault(videos.map((video) => video.playback_id));
  const {
    retrieveItemFromVault: retrieveSongAssetFromVault,
    fetchTokenSetForItem: fetchTokenSetForSongAsset,
  } = useTokenSetVault(songs?.map((song) => song.playback_id));

  const { errorDirectives } = useErrorDirectives();
  const { errors, heading } = findApplicableErrorDetails(
    errorDirectives,
    ERROR_KINDS.COURSE_PLAYER_UNAVAILABLE,
  );

  const activeVideo = _findVideo(videos, activeSubChapter);
  const onDeckVideo = _findVideo(videos, onDeckSubChapter);

  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  const activeTokenSet =
    activeVideo && retrieveVideoAssetFromVault(activeVideo.playback_id);
  const activePlaybackHistory = _findPlaybackHistory(
    playbackHistories,
    activeVideo,
  );

  // handle fetching token sets for active video only
  useEffect(() => {
    if (activeVideo) {
      fetchTokenSetForVideoAsset(activeVideo.playback_id, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeVideo]);

  useEffect(() => {
    function ensureStreamingVideoIsVisibleInNavigation(
      activeVideoPosition: TypeVideoPosition,
    ) {
      const { partId, chapterId } = courseNavigation.navigationPosition;
      const activeVideoExistsInDifferentPart =
        partId !== activeVideoPosition.partId;
      const activeVideoExistsInDifferentChapter =
        chapterId !== activeVideoPosition.chapterId;

      if (
        activeVideoExistsInDifferentPart ||
        activeVideoExistsInDifferentChapter
      ) {
        courseNavigation.navigateToPosition(
          activeVideoPosition.partId,
          activeVideoPosition.chapterId,
        );
      }
    }

    if (activeVideoPosition) {
      ensureStreamingVideoIsVisibleInNavigation(activeVideoPosition);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeVideoPosition]);

  const [enableAutoplay, setEnableAutoplay] = useState(false);

  function onVideoComplete() {
    if (activePlaybackHistory) {
      const muxVideoUpload = activeSubChapter?.fields.video.fields
        .muxVideoUpload as TypeSignedMuxVideoUpload;
      playbackHistoryMutations.update(
        activePlaybackHistory.id,
        muxVideoUpload.duration,
        new Date(),
      );
    }

    if (onDeckVideoPosition) {
      // if the on deck video begins a new part, navigate there
      if (onDeckVideoPosition.partId !== activeVideoPosition.partId) {
        courseNavigation.navigateToPosition(onDeckVideoPosition.partId);
      }

      // if the on deck video begins a new chapter, navigate there
      if (onDeckVideoPosition.chapterId !== activeVideoPosition.chapterId) {
        courseNavigation.navigateToChapter(onDeckVideoPosition.chapterId);
      }
    }

    reportActiveVideoCompleted();
  }

  function onVideoProgress(timeInSeconds: number) {
    if (!activeVideo) return;

    const formattedSeconds = Math.trunc(timeInSeconds);
    const isVideoPaused = formattedSeconds === activePlaybackHistory?.head;

    if (isVideoPaused) {
      return;
    }

    const muxVideoUpload: TypeSignedMuxVideoUpload =
      activeSubChapter?.fields.video.fields.muxVideoUpload;
    const { duration } = muxVideoUpload || {};
    const roundedDuration = Math.trunc(duration);
    const percentageComplete = calculatePercentage(
      formattedSeconds,
      roundedDuration,
      'floor',
    );

    // Preload next video's token when current video is at 50%
    if (
      percentageComplete >= PRELOAD_THRESHOLD * 100 &&
      onDeckVideo?.playback_id
    ) {
      fetchTokenSetForVideoAsset(onDeckVideo.playback_id, false);
    }

    capturePlaybackProgressEvent(PLAYBACK_EVENT_TYPES.PROGRESS, {
      title: activeSubChapter?.fields.title || '',
      percentComplete: percentageComplete,
      head: formattedSeconds,
    });

    if (activePlaybackHistory) {
      playbackHistoryMutations.update(
        activePlaybackHistory.id,
        timeInSeconds,
        undefined,
      );
    } else {
      playbackHistoryMutations.create(activeVideo.id);
    }
    if (!enableAutoplay) {
      setEnableAutoplay(true);
    }
  }

  const tabs = useMemo(() => {
    function isVideoActive(videoSysId: string) {
      return activeSubChapter?.sys.id === videoSysId;
    }

    function streamVideoOnDemand(videoSysId: string) {
      regenerateStreamingQueueFromVideo(videoSysId);
    }

    function getPlaybackHistoryForSubChapter(subChapter: TypeCourseSubChapter) {
      const video = _findVideo(videos, subChapter);
      return _findPlaybackHistory(playbackHistories, video);
    }

    function createProgressBar(
      subChapter: TypeCourseSubChapter,
    ): CourseSubChapterProgressBar {
      const playbackHistory = getPlaybackHistoryForSubChapter(subChapter);
      const { duration } = subChapter.fields.video.fields.muxVideoUpload;
      const roundedDuration = Math.trunc(duration);
      const { head } = playbackHistory || {};
      const percentageComplete = calculatePercentage(
        head,
        roundedDuration,
        'floor',
      );
      const hasEverBeenCompleted = !!playbackHistory?.completed_at;

      return {
        subChapterId: subChapter.sys.id,
        percentageComplete,
        isComplete: hasEverBeenCompleted,
      };
    }

    function getSubChapterProgressBars(
      courseChapter: Entry<TypeCourseChapterFields>,
    ): CourseSubChapterProgressBar[] {
      return courseChapter.fields.subChapters.map(createProgressBar);
    }

    return courseProduct.fields.courseParts.map((coursePart, i) => ({
      title: interpolate(coursePart.fields.titleWithSectionPrefix, {
        currentSection: i + 1,
        sectionCount: courseProduct.fields.courseParts.length,
      }),
      onSelectTab: () => courseNavigation.navigateToPosition(coursePart.sys.id),
      jsxContent: (
        <>
          {coursePart.fields.chapters.map((chapter, i) => (
            <CourseChapter
              key={chapter.sys.id}
              courseChapter={chapter}
              isFirstChapter={i === 0}
              isActiveChapter={
                chapter.sys.id === courseNavigation.navigationPosition.chapterId
              }
              isActiveVideoInChapter={chapter.fields.subChapters.some(
                (subChapter) => subChapter.sys.id === activeSubChapter?.sys.id,
              )}
              isVideoActive={isVideoActive}
              streamVideoOnDemand={streamVideoOnDemand}
              navigateToChapter={() =>
                courseNavigation.navigateToChapter(chapter.sys.id)
              }
              closeChapter={() => courseNavigation.navigateToChapter('')}
              progressBars={getSubChapterProgressBars(chapter)}
              isVideoPlaying={isVideoPlaying}
              setIsVideoPlaying={setIsVideoPlaying}
            />
          ))}
        </>
      ),
    }));
  }, [
    activeSubChapter?.sys.id,
    courseNavigation,
    courseProduct.fields.courseParts,
    playbackHistories,
    regenerateStreamingQueueFromVideo,
    videos,
    isVideoPlaying,
  ]);

  // it is expected that the course player should always have a video loaded in the player
  if (!activeSubChapter) {
    return (
      <Errors errors={errors} heading={heading} classes="container mx-auto" />
    );
  }

  return (
    <div data-testid="course-player-container">
      <div className="lg:grid lg:grid-cols-12 lg:gap-4">
        <div className="sticky top-[72px] z-10 lg:z-0 lg:col-span-7">
          <CourseVideo
            muxVideo={
              activeSubChapter.fields.video.fields as TypeSignedMuxVideoAsset
            }
            title={activeSubChapter.fields.title}
            seriesTitle={courseProduct.fields.title}
            playbackServiceVideoId={activeVideo?.id}
            onVideoComplete={onVideoComplete}
            onVideoProgress={onVideoProgress}
            refetchTokens={fetchTokenSetForVideoAsset}
            startTime={activePlaybackHistory?.head}
            userUuid={activePlaybackHistory?.user_uuid}
            tokenSet={activeTokenSet}
            isSigned
            shouldAutoplay={enableAutoplay}
            setIsVideoPlaying={setIsVideoPlaying}
          />
        </div>
        <div
          className="mb-8 mt-4 lg:col-span-5 lg:mx-0 lg:mb-0 lg:mt-0 lg:flex lg:flex-col"
          data-testid="course-player-navigation-container"
        >
          <div className="lg:relative lg:h-full">
            <div className="lg:absolute lg:left-0 lg:top-0 lg:flex lg:h-full lg:w-full lg:flex-col lg:overflow-y-scroll lg:overscroll-contain">
              <Tabs
                tabs={tabs}
                shouldUseActiveTabIndexOverride
                activeTabIndexOverride={courseNavigation.activePartIndex}
                setActiveTabIndexOverride={(idx) =>
                  courseNavigation.navigateToPosition(
                    courseProduct.fields.courseParts[idx].sys.id,
                  )
                }
                expandedTabDisplayType={ExpandedTabDisplayTypes.JSX}
                navigationDisplayType={NavigationDisplayTypes.Single}
              />
            </div>
          </div>
        </div>
      </div>
      {additionalSupport && (
        <BackgroundColorWrapper color="aqua-tint">
          <div
            className="container mx-auto py-20 lg:max-w-[1128px]"
            data-testid="course-player-additional-support-container"
          >
            <HeaderText
              text={additionalSupportSectionTitle}
              size={HeaderSize.H2}
              align="center"
            />
            <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {additionalSupport.map((additionalSupportItem) => (
                <CourseSupportingContent
                  key={additionalSupportItem.sys.id}
                  {...additionalSupportItem.fields}
                  fetchTokenSetForSongAsset={fetchTokenSetForSongAsset}
                  retrieveSongAssetFromVault={retrieveSongAssetFromVault}
                />
              ))}
            </div>
            {courseProduct.fields.disclaimerCopy && (
              <div className="container pt-20 sm:px-4 lg:mr-auto lg:max-w-[750px]">
                <RichText
                  content={
                    courseProduct.fields.disclaimerCopy.fields
                      .richText as Document
                  }
                />
              </div>
            )}
          </div>
        </BackgroundColorWrapper>
      )}
    </div>
  );
};

export const CoursePlayerWithData: FC<TypeCoursePlayerFields> = (fields) => {
  const { courseProduct } = fields;
  const { playgeniusProductAnchor } = courseProduct?.fields;
  const {
    data: mediaAssets,
    loading: mediaAssetsLoading,
    error: mediaAssetsError,
  } = useCourseMediaAssets(playgeniusProductAnchor);
  const {
    data: playbackHistoriesData,
    loading: playbackHistoriesLoading,
    error: playbackHistoriesError,
    mutations: playbackHistoryMutations,
  } = useCoursePlaybackHistories(playgeniusProductAnchor);
  const { errorDirectives } = useErrorDirectives();
  const { errors, heading } = findApplicableErrorDetails(
    errorDirectives,
    ERROR_KINDS.COURSE_PLAYER_UNAVAILABLE,
  );

  if (mediaAssetsLoading || playbackHistoriesLoading) {
    return <Loader loading />;
  }

  if (mediaAssetsError || playbackHistoriesError) {
    return (
      <Errors errors={errors} heading={heading} classes="container mx-auto" />
    );
  }

  const videos = mediaAssets?.media_assets?.filter(
    (media_asset) => media_asset.type === 'Video',
  );

  const songs = mediaAssets?.media_assets?.filter(
    (media_asset) => media_asset.type === 'Song',
  );

  if (!videos || videos.length === 0) {
    return (
      <Errors errors={errors} heading={heading} classes="container mx-auto" />
    );
  }

  const initialVideoId = _getInitialVideoId(
    videos,
    playbackHistoriesData?.playback_histories,
  );

  return (
    <CoursePlayer
      {...fields}
      videos={videos}
      songs={songs}
      playbackHistories={playbackHistoriesData?.playback_histories}
      playbackHistoryMutations={playbackHistoryMutations}
      initialVideoId={initialVideoId}
    />
  );
};

export const CoursePlayerTemplateAssignment: FC<TypeCoursePlayerFields> = (
  props,
) => {
  const { isPreviewPlayer } = props;

  if (isPreviewPlayer) {
    return <CoursePlayerPreview {...props} />;
  }

  return <CoursePlayerWithData {...props} />;
};

export default CoursePlayerTemplateAssignment;
